import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge } from '@theme/utilities';

/** @param {HTMLButtonElement} button */
function submitBuyNow(button) {
  if (button.disabled) return;
  const form = button.closest('form');
  if (!(form instanceof HTMLFormElement)) return;
  const variantInput = form.querySelector('input[name="id"]');
  if (!(variantInput instanceof HTMLInputElement) || variantInput.disabled || !variantInput.value) return;

  let returnTo = form.querySelector('input[name="return_to"]');
  if (!(returnTo instanceof HTMLInputElement)) {
    returnTo = document.createElement('input');
    returnTo.type = 'hidden';
    returnTo.name = 'return_to';
    form.appendChild(returnTo);
  }

  returnTo.value = '/checkout';
  button.disabled = true;
  HTMLFormElement.prototype.submit.call(form);
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-harper-product-buy-now]');
  if (button instanceof HTMLButtonElement) submitBuyNow(button);
});

/** @param {ParentNode} root */
function setupQuickAddActions(root) {
  root.querySelectorAll('[data-harper-product-buy-now]').forEach((button) => button.remove());

  const buyNowButton = root.querySelector('[ref="addToCartButton"]');
  if (!(buyNowButton instanceof HTMLButtonElement)) return;

  buyNowButton.classList.add('quick-add-modal__chrome-button');
  buyNowButton.type = 'button';
  buyNowButton.removeAttribute('name');
  buyNowButton.removeAttribute('on:click');
  buyNowButton.setAttribute('data-quick-add-buy-now', '');

  const buyNowLabel = buyNowButton.querySelector('.add-to-cart-text__content');
  if (buyNowLabel) buyNowLabel.textContent = 'Buy now';

  if (!buyNowButton.querySelector('.quick-add-modal__chrome-bar')) {
    const chromeBar = document.createElement('checkout-3d-bar');
    chromeBar.className = 'quick-add-modal__chrome-bar';
    chromeBar.setAttribute('aria-hidden', 'true');
    buyNowButton.prepend(chromeBar);
  }

  const wrapper = buyNowButton.closest('.add-to-cart-wrapper');
  if (!(wrapper instanceof HTMLElement)) return;

  let addToCartButton = wrapper.querySelector('[data-quick-add-add-to-cart]');
  if (!(addToCartButton instanceof HTMLButtonElement)) {
    addToCartButton = document.createElement('button');
    addToCartButton.type = 'submit';
    addToCartButton.name = 'add';
    addToCartButton.className = 'button quick-add-modal__secondary-atc';
    addToCartButton.setAttribute('data-quick-add-add-to-cart', '');

    const label = document.createElement('span');
    label.className = 'quick-add-modal__secondary-atc-label';
    label.textContent = 'Add to cart';

    addToCartButton.append(label);
    wrapper.prepend(addToCartButton);
  }

  addToCartButton.disabled = buyNowButton.disabled;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productFormComponent = productGrid.querySelector('product-form-component');
    const customVariantData = productGrid.querySelector('[data-quick-add-product-json]');
    const variantPicker = productGrid.querySelector('variant-picker') || customVariantData?.parentElement;
    const productPrice = productGrid.querySelector('product-price');
    const sourceTitle = /** @type {HTMLAnchorElement | null} */ (productGrid.querySelector('.view-product-title a'));
    if (!productFormComponent) return;

    const productTitle = document.createElement('a');
    productTitle.textContent = sourceTitle?.textContent?.trim() || this.dataset.productTitle || '';
    productTitle.href = sourceTitle?.href || this.productPageUrl;

    const titleWrapper = document.createElement('div');
    titleWrapper.className = 'view-product-title quick-add-modal__title';
    titleWrapper.appendChild(productTitle);

    const productHeader = document.createElement('header');
    productHeader.className = 'quick-add-modal__product-header';
    productHeader.appendChild(titleWrapper);
    if (productPrice) productHeader.appendChild(productPrice);

    const sizeChartView = this.#createSizeChartView(productGrid);
    productHeader.appendChild(sizeChartView.trigger);

    const mainView = document.createElement('div');
    mainView.className = 'quick-add-modal__main-view';
    mainView.setAttribute('data-quick-add-main-view', '');
    mainView.appendChild(productHeader);

    if (variantPicker) {
      variantPicker.classList.add('quick-add-modal__variant-picker');
      variantPicker.querySelectorAll('[data-size-chart-modal], [data-size-chart-trigger]').forEach((element) => element.remove());
      mainView.appendChild(variantPicker);
    }

    setupQuickAddActions(productFormComponent);

    mainView.appendChild(productFormComponent);

    productGrid.className = 'quick-add-product-shell';
    productGrid.replaceChildren(mainView, sizeChartView.panel);

    morph(modalContent, productGrid);
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) dialogComponent.resetSizeChart();
  }

  /** @param {Element} productGrid */
  #createSizeChartView(productGrid) {
    const chartModals = [...productGrid.querySelectorAll('[data-size-chart-modal]')];
    const chartModal =
      chartModals.find((modal) => modal.querySelector('img, [class*="size-chart-text"]')) || chartModals[0] || null;
    const sourceTrigger = productGrid.querySelector('[data-size-chart-trigger]');
    const sourceImage = /** @type {HTMLImageElement | null} */ (chartModal?.querySelector('img'));
    const sourceText = chartModal?.querySelector('[class*="size-chart-text"]');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'button quick-add-modal__size-chart-trigger';
    trigger.setAttribute('data-quick-add-size-chart-open', '');
    trigger.textContent = sourceTrigger?.textContent?.trim() || 'Size chart';

    const panel = document.createElement('section');
    panel.className = 'quick-add-modal__size-chart-view';
    panel.setAttribute('data-quick-add-size-chart-view', '');
    panel.setAttribute('aria-hidden', 'true');

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'button button-unstyled quick-add-modal__size-chart-close';
    closeButton.setAttribute('data-quick-add-size-chart-close', '');
    closeButton.setAttribute('aria-label', 'Close size chart');
    closeButton.innerHTML = '<span aria-hidden="true">&times;</span>';

    const heading = document.createElement('h2');
    heading.className = 'quick-add-modal__size-chart-heading';
    heading.textContent = trigger.textContent;

    const body = document.createElement('div');
    body.className = 'quick-add-modal__size-chart-body';

    if (sourceImage) {
      const image = /** @type {HTMLImageElement} */ (sourceImage.cloneNode(true));
      image.className = 'quick-add-modal__size-chart-image';
      image.loading = 'eager';
      body.appendChild(image);
    }

    if (sourceText) {
      const text = document.createElement('div');
      text.className = 'quick-add-modal__size-chart-text';
      text.innerHTML = sourceText.innerHTML;
      body.appendChild(text);
    }

    if (!sourceImage && !sourceText) {
      const emptyText = document.createElement('p');
      emptyText.className = 'quick-add-modal__size-chart-empty';
      emptyText.textContent = 'Size chart information is not available for this product.';
      body.appendChild(emptyText);
    }

    panel.append(closeButton, heading, body);
    return { trigger, panel };
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {HTMLElement | null} */
  #sizeChartTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('click', this.#handleSizeChartClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.resetSizeChart();
    this.closeDialog();
  };

  #handleSizeChartClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target : null);
    const variantButton = target?.closest('[data-option-position][data-option-value]');
    const openButton = target?.closest('[data-quick-add-size-chart-open]');
    const closeButton = target?.closest('[data-quick-add-size-chart-close]');
    const buyNowButton = target?.closest('[data-quick-add-buy-now]');

    if (variantButton instanceof HTMLButtonElement) {
      this.#selectCustomVariant(variantButton);
    } else if (openButton instanceof HTMLElement) {
      this.#sizeChartTrigger = openButton;
      this.showSizeChart();
    } else if (closeButton) {
      this.hideSizeChart();
    } else if (buyNowButton instanceof HTMLButtonElement) {
      this.#buyNow(buyNowButton);
    }
  };

  /** @param {HTMLButtonElement} button */
  #buyNow(button) {
    submitBuyNow(button);
  }

  /** @param {HTMLButtonElement} selectedButton */
  #selectCustomVariant(selectedButton) {
    if (selectedButton.disabled) return;
    const picker = selectedButton.closest('.quick-add-modal__variant-picker');
    const dataNode = picker?.querySelector('[data-quick-add-product-json]');
    if (!(picker instanceof HTMLElement) || !(dataNode instanceof HTMLScriptElement)) return;

    let product;
    try {
      product = JSON.parse(dataNode.textContent || '{}');
    } catch (error) {
      console.warn('Quick add variant data is invalid.', error);
      return;
    }

    const position = selectedButton.dataset.optionPosition;
    if (!position || !Array.isArray(product.variants)) return;

    picker.querySelectorAll(`[data-option-position="${position}"]`).forEach((button) => {
      button.classList.toggle('selected', button === selectedButton);
      button.setAttribute('aria-pressed', String(button === selectedButton));
    });

    const selectedOptions = {};
    picker.querySelectorAll('[data-option-position].selected').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const optionPosition = button.dataset.optionPosition;
      if (optionPosition) selectedOptions[optionPosition] = button.dataset.optionValue;
    });

    const variant = product.variants.find((item) =>
      item.options.every((option, index) => option === selectedOptions[String(index + 1)])
    );

    const variantInput = this.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variant ? String(variant.id) : '';
      variantInput.setAttribute('value', variantInput.value);
      variantInput.disabled = !variant?.available;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const addButton = this.querySelector('product-form-component [ref="addToCartButton"]');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = !variant?.available;
    const secondaryButton = this.querySelector('[data-quick-add-add-to-cart]');
    if (secondaryButton instanceof HTMLButtonElement) secondaryButton.disabled = !variant?.available;

    const stockData = JSON.parse(picker.querySelector('[data-variant-stock-json]')?.textContent || '{}');

    picker.querySelectorAll('[data-option-position][data-option-value]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const testOptions = { ...selectedOptions, [button.dataset.optionPosition]: button.dataset.optionValue };
      const matchingVariant = product.variants.find((item) =>
        item.options.every((option, index) => option === testOptions[String(index + 1)])
      );
      button.disabled = false;
      button.dataset.optionAvailable = String(Boolean(matchingVariant?.available));
      const stock = button.querySelector('[data-size-stock]');
      if (stock) {
        stock.textContent = matchingVariant?.available ? stockData[matchingVariant.id] || '' : '';
        stock.hidden = !stock.textContent;
      }
    });
  }

  showSizeChart() {
    const panel = this.querySelector('[data-quick-add-size-chart-view]');
    if (!(panel instanceof HTMLElement)) return;

    this.setAttribute('size-chart-open', '');
    panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      const closeButton = panel.querySelector('[data-quick-add-size-chart-close]');
      if (closeButton instanceof HTMLElement) closeButton.focus();
    });
  }

  hideSizeChart() {
    const panel = this.querySelector('[data-quick-add-size-chart-view]');
    this.removeAttribute('size-chart-open');
    panel?.setAttribute('aria-hidden', 'true');
    this.#sizeChartTrigger?.focus();
  }

  resetSizeChart() {
    const panel = this.querySelector('[data-quick-add-size-chart-view]');
    this.removeAttribute('size-chart-open');
    panel?.setAttribute('aria-hidden', 'true');
    this.#sizeChartTrigger = null;
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    setupQuickAddActions(this);
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
